import type { ReportParameterDefinition, ResolvedParameterValues } from '@/types';

/**
 * What the report itself would use for a parameter, and how firmly we can say so.
 *
 * Three answers, because SSRS genuinely has three cases and flattening them would mislead:
 * a value pinned in the report definition, a value the report computes each time it runs, and no
 * default at all. The middle one is the common one — seven of the nine parameters on "Any Client
 * Detail (Custom View)" compute theirs — and it is the one that must never be shown as though it
 * were fixed, because it is not: that report's From Date resolves to today and its To Date to
 * today + 60 days, so both are different tomorrow.
 */
export type DefaultInfo =
  | { kind: 'none' }
  | { kind: 'fixed'; values: string[] }
  | { kind: 'computed'; values: string[] }
  | { kind: 'computed-unknown' };

export function describeDefault(
  def: ReportParameterDefinition,
  resolved: ResolvedParameterValues | undefined,
  echoed: boolean
): DefaultInfo {
  // Only the report's answer when the editor did NOT hand this parameter's own value to the
  // server. Where it did, the server hands it straight back as the "default", and repeating that
  // would tell the user the report defaults to whatever the subscription already stores.
  const live = echoed ? [] : (resolved?.defaultValues ?? []).filter(v => v !== '');

  if (def.hasDynamicDefault)
    return live.length > 0 ? { kind: 'computed', values: live } : { kind: 'computed-unknown' };

  if (def.defaultValues.length > 0) return { kind: 'fixed', values: def.defaultValues };

  // No catalogue default and no dynamic flag, but the server named one anyway. That happens on the
  // PBIRS fallback path, whose payload carries no DynamicDefaultValue to read — so believe the
  // value and describe it as computed, which is the honest reading of where it came from.
  return live.length > 0 ? { kind: 'computed', values: live } : { kind: 'none' };
}

export function sameValues(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/**
 * The one word the label row spends on a default, or null when there is nothing to spend it on.
 *
 * "computed" is the word that earns its place: it says the value moves, which is the single fact
 * about an SSRS default that a control cannot show and a reader cannot guess. "default" is the
 * quieter case — a value pinned in the report, which is not news until you ask for it. A parameter
 * the report has no default for gets no affordance at all, which is what removed the line that
 * used to appear under most of the form saying exactly that.
 */
export function defaultAffordanceLabel(info: DefaultInfo): string | null {
  switch (info.kind) {
    case 'none': return null;
    case 'fixed': return 'default';
    default: return 'computed';
  }
}
